import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { Controller, FieldPathValue, Path } from 'react-hook-form';
import { BaseControlledInputProps } from '../BasicControlledInputProps';
import FormFieldWrappers from '../FormFieldWrappers';
import { parseError } from '../form-utils/errorParser';
import { useFormTracking, useFormOptionTracking } from '../hooks';

export interface RadioOption<T> {
  label: string;
  value: T;
  callback?: () => void;
  disabled?: boolean;
}

export interface RadioGroupProps<T> {
  label?: string;
  /**
   * Array of options representing the radio buttons;
   * include a label and value for each option
   */
  options: RadioOption<T>[];
  /**
   * Whether to display the radio group horizontally
   */
  row?: boolean;
}

export type ControlledRadioGroupProps<
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
> = Omit<BaseControlledInputProps<FORM_TYPE, FIELD_NAME>, 'label'> &
  RadioGroupProps<FieldPathValue<FORM_TYPE, FIELD_NAME>>;

export const ControlledRadioGroup = <
  FORM_TYPE,
  FIELD_NAME extends Path<FORM_TYPE>
>({
  name,
  control,
  options,
  defaultValue = undefined,
  required,
  disabled = false,
  row = false,
  errors = undefined,
  label,
  ariaLabel,
  ariaRole,
  trackingName,
  sensitive = true,
}: ControlledRadioGroupProps<FORM_TYPE, FIELD_NAME>) => {
  // Auto-generate tracking props if trackingName is provided
  const trackingProps = useFormTracking(trackingName);
  const { generateOptionTracking } = useFormOptionTracking(
    trackingName,
    sensitive,
    options
  );

  function handleChange(event, currentValue, onChange) {
    const clickedValue = event.target.value;
    if (clickedValue === currentValue) {
      onChange(null);
    } else {
      onChange(clickedValue);
    }
  }

  return (
    <FormFieldWrappers.FieldWrapper style={{ paddingBottom: '3.5px' }}>
      <FormControl error={!!parseError(name, errors)}>
        <Controller
          name={name}
          control={control}
          rules={{ required: required ? `${name} is required` : false }}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <>
                {label && (
                  <div style={{ marginTop: '-10px' }}>
                    <Typography
                      sx={{
                        fontFamily: `'Montserrat', 'Lato', 'sans-serif'`,
                        color: 'rgba(0, 0, 0, 0.6)',
                        fontSize: '12px;',
                      }}
                      variant="bodyMedium"
                    >
                      {label}
                    </Typography>
                  </div>
                )}

                <RadioGroup
                  {...trackingProps}
                  row={row}
                  defaultValue={defaultValue}
                  value={value}
                  role={ariaRole || 'radiogroup'}
                  aria-label={ariaLabel || 'Radio group label undefined'}
                  onChange={event => handleChange(event, value, onChange)}
                  sx={{ justifyContent: 'space-between' }}
                >
                  {options.map(option => {
                    return (
                      <FormControlLabel
                        key={`option-${option.value}`}
                        label={option.label}
                        value={option.value}
                        disabled={
                          option.disabled !== undefined
                            ? option.disabled
                            : disabled
                        }
                        control={
                          <Radio
                            {...generateOptionTracking(
                              option.value,
                              option.label
                            )}
                            aria-checked={value === option.value}
                            role="radio"
                            onClick={event => {
                              handleChange(event, value, onChange);
                              if (option.callback) {
                                option.callback();
                              }
                            }}
                          />
                        }
                      />
                    );
                  })}
                  {error && <FormHelperText>{error?.message}</FormHelperText>}
                </RadioGroup>
              </>
            );
          }}
        />
      </FormControl>
    </FormFieldWrappers.FieldWrapper>
  );
};
